import { z } from 'zod';
import { type Label, UserRole, type UserStoreRelation } from '../../../data/store';

/**
 * react-hook-form does not support arrays of primitives, only objects. So we have to transform the emails into objects
 * with the email in the `value` property, and then transform these back in our onSubmit function.
 *
 * https://github.com/orgs/react-hook-form/discussions/7586
 */
export type FormValues = {
  name: string;
  labels: Label[];
  users: UserStoreRelation[];
};

// form validation rules.
//
// Yup's `.required()` on a string rejects "" as well as undefined, which is what
// carries each of these messages; Zod's z.string() only rejects the wrong type,
// so the emptiness check has to be explicit as `.min(1, msg)`. Translating
// `.required(msg)` to a bare z.string() would silently let a blank store name
// or email through.
export const validationSchema = z.object({
  name: z.string().min(1, 'Store name is required'),
  labels: z.array(
    z.object({
      id: z.number('Label ID is required'),
      label: z.string().min(1, 'Label name is required'),
      color: z.string().min(1, 'Label color is required'),
    })
  ),
  users: z
    .array(
      z.object({
        email: z.string().min(1, 'Email is required').pipe(z.email('Invalid email address')),
        relation: z.enum(UserRole, 'Invalid relation'),
        store_id: z.string(),
      })
    )
    .max(10, 'Maximum 10 users allowed'),
});
